<template>
  <div>
    <canvas ref="chartCanvas" width="400" height="200"></canvas>
  </div>
</template>

<script>
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  PieController,
  ArcElement,
  LineController,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

// Register required components globally
Chart.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  PieController,
  ArcElement,
  LineController,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

export default {
  props: ["record", "chartType"],
  data() {
    return {
      chart: null,
    };
  },
  mounted() {
    this.createChart();
  },
  methods: {
    createChart() {
      const canvas = this.$refs.chartCanvas;
      if (!canvas) {
        console.warn(`Canvas element not found for chartType: ${this.chartType}`);
        return;
      }

      const ctx = canvas.getContext("2d");

      let chartData = {};
      let type = "bar"; // Default chart type
      switch (this.chartType) {
        case "audio": {
          // Audio: Bar chart with category-based x-axis
          chartData = {
            labels: this.record.audio_based_analysis_results.map((res) => res.label),
            datasets: [
              {
                label: "Audio Emotion Analysis",
                data: this.record.audio_based_analysis_results.map((res) => res.score),
                backgroundColor: "rgba(75, 192, 192, 0.2)",
                borderColor: "rgba(75, 192, 192, 1)",
                borderWidth: 1,
              },
            ],
          };
          break;
        }
        case "text": {
          // Text: Category-based x-axis for different models
          const textAnalysis = this.record.text_based_analysis_results;
          if (this.record.model === "TextBlob") {
            type = "pie";
            chartData = {
              labels: ["Polarity", "Subjectivity"],
              datasets: [
                {
                  data: [textAnalysis.TextBlob.polarity, textAnalysis.TextBlob.subjectivity],
                  backgroundColor: ["#FF6384", "#36A2EB"],
                },
              ],
            };
          } else if (this.record.model === "VADER") {
            type = "line";
            chartData = {
              labels: ["Neg", "Neu", "Pos", "Compound"],
              datasets: [
                {
                  label: "VADER Analysis",
                  data: [
                    textAnalysis.VADER.neg,
                    textAnalysis.VADER.neu,
                    textAnalysis.VADER.pos,
                    textAnalysis.VADER.compound,
                  ],
                  borderColor: "#36A2EB",
                  fill: false,
                },
              ],
            };
          } else {
            type = "bar";
            const labels = Object.keys(textAnalysis.HuggingFace || textAnalysis.Flair || {});
            const data = labels.map((key) => textAnalysis.HuggingFace[key] || textAnalysis.Flair[key]);
            chartData = {
              labels,
              datasets: [
                {
                  label: "Text Model Analysis",
                  data,
                  backgroundColor: "rgba(153, 102, 255, 0.2)",
                  borderColor: "rgba(153, 102, 255, 1)",
                  borderWidth: 1,
                },
              ],
            };
          }
          break;
        }
        case "facial": {
          // Facial: Line chart with time-based x-axis and emotion zones
          type = "line";

          const emotions = ["Anger", "Disgust", "Sad", "Neutral", "Happy", "Surprise"];
          const facialResults =
            this.record.facial_analysis_results || this.record.description_facial_results;

          chartData = {
            labels: Array.from({ length: facialResults.length }, (_, i) => i + 1), // Time steps
            datasets: Object.keys(facialResults[0]).map((emotion, idx) => ({
              label: emotions[idx] || emotion,
              data: facialResults.map((frame) => frame[emotion] + idx), // Offset to emotion zone
              borderColor: this.getRandomColor(),
              fill: false,
            })),
          };

          break;
        }
      }

      this.chart = new Chart(ctx, {
        type,
        data: chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x:
              this.chartType === "facial"
                ? {
                    type: "linear",
                    title: {
                      display: true,
                      text: "Time (frames)",
                    },
                  }
                : {
                    title: {
                      display: true,
                      text: "Categories",
                    },
                  }, // Use category-based x-axis for non-facial charts
            y:
              this.chartType === "facial"
                ? {
                    type: "linear",
                    ticks: {
                      callback: (value) => {
                        const emotions = ["Anger", "Disgust", "Sad", "Neutral", "Happy", "Surprise"];
                        const zone = Math.floor(value);
                        return emotions[zone] || ""; // Emotion label for each zone
                      },
                      stepSize: 1,
                    },
                    title: {
                      display: true,
                      text: "Emotion Zones",
                    },
                  }
                : undefined, // Use default y-axis for non-facial charts
          },
        },
      });
    },
    getRandomColor() {
      const letters = "0123456789ABCDEF";
      return (
        "#" +
        Array.from({ length: 6 })
          .map(() => letters[Math.floor(Math.random() * 16)])
          .join("")
      );
    },
  },
  beforeUnmount() {
    if (this.chart) {
      this.chart.destroy();
    }
  },
};
</script>
